import NavbarDropdownMenu from "@components/Navbar/NavbarDropdownMenu";
import { Button, Icon, TextButton } from "@components/library";
import { Z_INDICES } from "@constants";
import haloLogo from "@images/logos/halo_logo.svg";
import { RootState } from "@redux/store";
import { proposal_review_system_path } from "@routes/routes";
import { marketplacePath } from "@routes/utils";
import { SegmentEventName } from "@tsTypes/__generated__/enums";
import { UserRole } from "@tsTypes/users";
import { track } from "@utils/appUtils";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import { COLORS, STYLES, WINDOW_DIMENSIONS } from "src/constants";
import styled from "styled-components";
import LoginSignUpModal from "../LoginSignUpModal";
import LoggedOutMobileMenu from "./LoggedOutMobileMenu";
import NavbarLink from "./NavbarLink";
import { NAVBAR_BORDER_PATHS, NAVBAR_HIDDEN_PATHS } from "./navbarLists";
import useNavbarLinks from "./useNavbarLinks";

interface Props {
  shouldIgnoreHiddenPaths?: boolean;
}

const Navbar = ({ shouldIgnoreHiddenPaths }: Props) => {
  const [isFixed, setIsFixed] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const currentUser = useSelector((state: RootState) => state.profiles.currentUser);
  const isTablet = useSelector((state: RootState) => state.window.isTablet);

  const { pathname } = useLocation();

  const navbarLinks = useNavbarLinks();

  const isUserLoggedOut = !currentUser.id;

  const handleScroll = () => {
    if (isUserLoggedOut && window.scrollY >= 720) {
      setIsFixed(true);
    } else if (window.scrollY === 0) {
      setIsFixed(false);
    }
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [pathname]);

  const shouldHideNavbar =
    NAVBAR_HIDDEN_PATHS.some((path) => pathname !== "/" && pathname.startsWith(path)) &&
    !shouldIgnoreHiddenPaths;
  if (shouldHideNavbar) return null;

  const hasBorder = NAVBAR_BORDER_PATHS.some(
    (path) => pathname !== "/" && pathname.startsWith(path)
  );

  const showBackToProposalReviewDashboard =
    currentUser.role === UserRole.SPONSOR &&
    currentUser.profile_info.verified &&
    currentUser.profile_info.commenter &&
    !pathname.startsWith("/review_system");

  return (
    <>
      <LoginSignUpModal />
      <LoggedOutMobileMenu isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)} />
      <Spacer isFixed={isFixed} />
      <Container isFixed={isFixed} hasBorder={hasBorder} data-testid="navbar-main">
        <Links>
          {isTablet &&
            (isUserLoggedOut ? (
              <LoggedOutMobileMenuButton type="button" onClick={() => setIsMobileMenuOpen(true)}>
                <Icon name="Burger" size="lg" />
              </LoggedOutMobileMenuButton>
            ) : (
              <NavbarDropdownMenu />
            ))}
          {isUserLoggedOut ? (
            <a href="/">
              <Logo src={haloLogo} alt="Halo logo" />
            </a>
          ) : (
            <Link to={marketplacePath()}>
              <Logo src={haloLogo} alt="Halo logo" />
            </Link>
          )}
          {showBackToProposalReviewDashboard && (
            <TextButtonLink to={proposal_review_system_path()}>
              <TextButton
                text="Back to proposal review"
                iconName="Arrow Left"
                iconPosition="left"
              />
            </TextButtonLink>
          )}
          <NavbarLinks $isLoggedOut={isUserLoggedOut}>
            {isTablet
              ? null
              : navbarLinks.map((link) => (
                  <NavbarLink
                    key={link.to}
                    to={link.to}
                    dropdownItems={link.dropdownItems}
                    withNewTag={link.withNewTag}
                    asAnchor={link.asAnchor}
                    activeStylesMatchType={link.activeStylesMatchType}
                    data-testid={link["data-testid"]}
                  >
                    {link.label}
                  </NavbarLink>
                ))}
          </NavbarLinks>
          {isUserLoggedOut ? (
            <SignInButtons>
              {!isTablet && (
                <>
                  <Button
                    to="/contact"
                    isExternalLink
                    variant="ghost"
                    onClick={() => {
                      track(SegmentEventName.CLICK, {
                        ui_component: "Contact Sales",
                        react_component: "Navbar",
                      });
                    }}
                  >
                    Contact sales
                  </Button>
                  <SignInButtonContainer>
                    <Button
                      to="/login"
                      isExternalLink
                      variant="outline"
                      onClick={() => {
                        track(SegmentEventName.CLICK, {
                          ui_component: "Sign In",
                          react_component: "Navbar",
                        });
                      }}
                    >
                      Sign in
                    </Button>
                  </SignInButtonContainer>
                </>
              )}
              <Button
                variant="marketing"
                to="/get-started"
                isExternalLink
                onClick={() => {
                  track(SegmentEventName.CLICK, {
                    ui_component: "Join Now",
                    react_component: "Navbar",
                  });
                }}
              >
                Join now
              </Button>
            </SignInButtons>
          ) : (
            !isTablet && <NavbarDropdownMenu />
          )}
        </Links>
      </Container>
    </>
  );
};

export default Navbar;

export const NAVBAR_HEIGHT = 84;
const Spacer = styled.div`
  height: ${NAVBAR_HEIGHT}px;
  position: ${({ isFixed }) => (isFixed ? "static" : "absolute")};
`;
const Container = styled.div`
  display: flex;
  align-items: center;
  ${({ isFixed }) =>
    isFixed &&
    `
      position: fixed;
      box-shadow: ${STYLES.SHADOW_CARD};
    `}
  top: ${({ isFixed }) => (isFixed ? 0 : `-${NAVBAR_HEIGHT}px`)};
  height: ${NAVBAR_HEIGHT}px;
  width: 100%;
  padding: 0 ${WINDOW_DIMENSIONS.PAGE_SIDE_PADDING}px;
  background-color: ${COLORS.WHITE};
  border-bottom: 1px solid ${({ hasBorder }) => (hasBorder ? COLORS.NEUTRAL_200 : "transparent")};
  z-index: ${Z_INDICES.APP_NAV_HEADER};
  transition: top 0.3s ease, box-shadow 0.2s ease;
  ${WINDOW_DIMENSIONS.TABLET_MEDIA_QUERY} {
    padding: 0 24px;
  }
  ${WINDOW_DIMENSIONS.MOBILE_MEDIA_QUERY} {
    padding: 0 16px 0 10px;
  }
`;
const Links = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  height: 100%;
  margin: 0 auto;
`;
const Logo = styled.img`
  height: 32px;
  min-width: fit-content;
`;
const NavbarLinks = styled.div`
  display: flex;
  gap: 16px;
  height: 100%;
  margin-left: ${({ $isLoggedOut }) => ($isLoggedOut ? "36px" : "auto")};
`;
const SignInButtons = styled.div`
  display: flex;
  gap: 8px;
  min-width: fit-content;
  margin-left: auto;
`;
const SignInButtonContainer = styled.div`
  box-shadow: ${STYLES.SHADOW_CARD};
`;
const LoggedOutMobileMenuButton = styled.button`
  border: none;
  background-color: transparent;
  &:focus {
    outline: none;
  }
`;
const TextButtonLink = styled(Link)`
  display: flex;
  align-items: center;
  height: 100%;
  margin: 0 auto 0 36px;
  &:hover {
    text-decoration: none;
  }
`;
